import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import {
  BillingProcess,
  BillingProcessCustomerGroup,
  BillingProcessGroupPortfolioMapping,
  BillingProcessInvoiceSettings,
} from 'src/app/models/billing-process';
import { BillingProcessService } from 'src/app/services/billing-process.service';
import { FinancesService } from 'src/app/services/finances.service';
import { StorageUtil } from 'src/app/utils/storage.util';
import { MessageUtil } from 'src/app/utils/message.util';

interface PortfolioOption {
  label: string;
  value: number;
}

interface GroupRow {
  groupId: number | null;
  groupName: string;
  customerCount: number;
  billingPortfolioId: number | null;
}

@Component({
  selector: 'app-wizard-step-invoice-data',
  templateUrl: './wizard-step-invoice-data.component.html',
  styleUrls: ['./wizard-step-invoice-data.component.scss'],
})
export class WizardStepInvoiceDataComponent implements OnInit {
  @Input() process: BillingProcess;
  @Output() next = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();

  referenceDate: Date | null = null;
  competenceDate: Date | null = null;
  dueDate: Date | null = null;
  separatelyByOrder = false;
  autoSendEmail = false;
  generateBoletos = false;
  skipInvoiceGeneration = false;

  monthOptions: Array<{ label: string; value: number }> = [];
  yearOptions: Array<{ label: string; value: number }> = [];
  portfolioOptions: PortfolioOption[] = [];
  groupRows: GroupRow[] = [];
  loadingPortfolios = false;
  loadingGroups = false;

  saving = false;

  private readonly monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
  ];
  dueDateMonth: number | null = null;
  dueDateYear: number | null = null;

  constructor(
    private billingProcessService: BillingProcessService,
    private financesService: FinancesService,
    private spinner: NgxSpinnerService,
    private messageService: MessageUtil,
  ) {}

  ngOnInit(): void {
    this.buildOptions();
    const existing = this.process.invoiceSettings;
    if (existing) {
      this.referenceDate = parseISODate(existing.referenceDate);
      this.competenceDate = parseISODate(existing.competenceDate);
      this.dueDate = parseISODate(existing.dueDate);
      this.dueDateMonth = existing.dueDateMonth ?? null;
      this.dueDateYear = existing.dueDateYear ?? null;
      this.separatelyByOrder = !!existing.separatelyByOrder;
      this.autoSendEmail = !!existing.autoSendEmail;
      this.generateBoletos = !!existing.generateBoletos;
      this.skipInvoiceGeneration = !!existing.skipInvoiceGeneration;
    } else {
      this.referenceDate = new Date();
      this.competenceDate = new Date(
        this.process.referenceYear,
        this.process.referenceMonth - 1,
        1,
      );
    }
    if (this.dueDateMonth == null || this.dueDateYear == null) {
      let m = this.process.referenceMonth + 1;
      let y = this.process.referenceYear;
      if (m > 12) { m = 1; y += 1; }
      this.dueDateMonth = m;
      this.dueDateYear = y;
    }

    if (this.generateBoletos && !this.skipInvoiceGeneration) {
      this.loadBoletosData();
    }
  }

  onToggleGenerateBoletos(): void {
    if (this.generateBoletos && !this.skipInvoiceGeneration
        && (this.portfolioOptions.length === 0 || this.groupRows.length === 0)) {
      this.loadBoletosData();
    }
  }

  private loadBoletosData(): void {
    this.loadPortfolios();
    this.loadGroups();
  }

  private loadPortfolios(): void {
    this.loadingPortfolios = true;
    const marina = StorageUtil.getMarina();
    let company: any = null;
    if (this.process.marinaCompanyId != null && marina?.marinaCompanies) {
      company = marina.marinaCompanies.find((c: any) => c.id === this.process.marinaCompanyId) ?? null;
    }
    this.financesService.getAllBilingPortifolio(company).subscribe({
      next: (data: any[]) => {
        this.portfolioOptions = (data ?? []).map((w) => ({
          label: w.nome,
          value: w.idCarteiraCobranca,
        }));
        this.loadingPortfolios = false;
      },
      error: (err) => {
        this.loadingPortfolios = false;
        this.messageService.generateMessage(
          'error', 'SUMMARY.ERROR',
          err?.error?.message ?? 'Erro ao carregar carteiras de cobrança.');
      },
    });
  }

  private loadGroups(): void {
    this.loadingGroups = true;
    this.billingProcessService.customerGroups(this.process.id).subscribe({
      next: (groups: BillingProcessCustomerGroup[]) => {
        const existing = this.process.invoiceSettings?.groupPortfolioMappings ?? [];
        this.groupRows = (groups ?? []).map((g) => {
          const saved = existing.find((m) => (m.groupId ?? null) === (g.groupId ?? null));
          return {
            groupId: g.groupId ?? null,
            groupName: g.groupName ?? 'Sem grupo',
            customerCount: g.customerCount ?? 0,
            billingPortfolioId: saved?.billingPortfolioId ?? null,
          };
        });
        this.loadingGroups = false;
      },
      error: (err) => {
        this.loadingGroups = false;
        this.messageService.generateMessage(
          'error', 'SUMMARY.ERROR',
          err?.error?.message ?? 'Erro ao carregar grupos de faturamento.');
      },
    });
  }

  private buildOptions(): void {
    this.monthOptions = this.monthNames.map((label, idx) => ({ label, value: idx + 1 }));
    const current = new Date().getFullYear();
    const years: Array<{ label: string; value: number }> = [];
    for (let y = current - 1; y <= current + 2; y++) {
      years.push({ label: String(y), value: y });
    }
    this.yearOptions = years;
  }

  isValid(): boolean {
    if (this.skipInvoiceGeneration) return true;
    if (!this.referenceDate || !this.competenceDate) return false;
    if (!this.dueDate && (!this.dueDateMonth || !this.dueDateYear)) return false;
    if (this.generateBoletos) {
      if (this.groupRows.length === 0) return false;
      if (this.groupRows.some((g) => g.billingPortfolioId == null)) return false;
    }
    return true;
  }

  proximo(): void {
    if (!this.isValid() || this.saving) return;
    this.saving = true;
    this.spinner.show();
    const mappings: BillingProcessGroupPortfolioMapping[] = this.generateBoletos
      ? this.groupRows.map((g) => ({
          groupId: g.groupId,
          groupName: g.groupName,
          billingPortfolioId: g.billingPortfolioId,
        }))
      : [];
    const payload: { invoiceSettings: BillingProcessInvoiceSettings } = {
      invoiceSettings: {
        referenceDate: toISODate(this.referenceDate),
        competenceDate: toISODate(this.competenceDate),
        dueDate: toISODate(this.dueDate),
        dueDateMonth: this.dueDateMonth,
        dueDateYear: this.dueDateYear,
        separatelyByOrder: this.separatelyByOrder,
        autoSendEmail: this.autoSendEmail,
        generateBoletos: this.generateBoletos,
        skipInvoiceGeneration: this.skipInvoiceGeneration,
        groupPortfolioMappings: mappings,
      },
    };
    this.billingProcessService.patch(this.process.id, payload).subscribe({
      next: (updated) => {
        this.spinner.hide();
        this.saving = false;
        this.process.invoiceSettings = updated.invoiceSettings;
        this.next.emit();
      },
      error: (err) => {
        this.spinner.hide();
        this.saving = false;
        this.messageService.generateMessage(
          'error', 'SUMMARY.ERROR',
          err?.error?.message ?? 'Erro ao salvar dados de faturamento.');
      },
    });
  }
}

function parseISODate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return new Date(s);
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function toISODate(d: Date | null): string | null {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
